import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  CONSENT_OPEN_EVENT,
  getConsent,
  setConsent,
  type ConsentValue,
} from "@/lib/cookieConsent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [externalMedia, setExternalMedia] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const saved = getConsent();
    setExternalMedia(saved === "all");
    if (saved === null) setVisible(true);

    const open = () => {
      setExternalMedia(getConsent() === "all");
      setVisible(true);
    };
    window.addEventListener(CONSENT_OPEN_EVENT, open);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, open);
  }, []);

  useEffect(() => {
    if (!visible) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && getConsent() !== null) setVisible(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus();
    };
  }, [visible]);

  if (!visible) return null;

  const choose = (value: ConsentValue) => {
    setConsent(value);
    setVisible(false);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-primary-deep/55 p-3 sm:items-center sm:p-6"
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-desc"
        tabIndex={-1}
        className="w-full max-w-3xl rounded-lg border border-border bg-card p-5 text-card-foreground shadow-elegant outline-none sm:p-6"
      >
        <h2 id="cookie-banner-title" className="font-display text-xl font-bold text-primary">
          Ihre Privatsphäre
        </h2>
        <p id="cookie-banner-desc" className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Wir nutzen technisch notwendige Speicherungen. Mit Ihrer Einwilligung laden wir zusätzlich Videos von Vimeo und Karten von OpenStreetMap. Ihre Auswahl können Sie jederzeit über „Cookie-Einstellungen“ im Footer ändern. Mehr in der{" "}
          <Link to="/datenschutz" className="underline underline-offset-2 hover:text-primary">
            Datenschutzerklärung
          </Link>
          .
        </p>

        <div className="mt-5 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-foreground">
              <strong>Notwendig</strong> – Speicherung Ihrer Auswahl
            </p>
            <span
              className="relative h-6 w-11 shrink-0 rounded-full bg-muted-foreground/50"
              role="switch"
              aria-checked="true"
              aria-label="Notwendige Speicherung, immer aktiv"
              aria-disabled="true"
            >
              <span className="absolute right-0.5 top-0.5 h-5 w-5 rounded-full bg-card shadow-sm" />
            </span>
          </div>

          <div className="flex items-center justify-between gap-4">
            <label htmlFor="external-media-consent" className="cursor-pointer text-sm text-foreground">
              <strong>Externe Medien</strong> – Vimeo-Videos und OpenStreetMap-Karten
            </label>
            <button
              id="external-media-consent"
              type="button"
              role="switch"
              aria-checked={externalMedia}
              aria-label="Externe Medien erlauben"
              onClick={() => setExternalMedia((enabled) => !enabled)}
              className={`relative h-6 w-11 shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${externalMedia ? "bg-primary" : "bg-muted-foreground/50"}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-card shadow-sm transition-transform ${externalMedia ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>
        </div>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => choose(externalMedia ? "all" : "necessary")}>
            Auswahl speichern
          </Button>
          <Button variant="outline" onClick={() => choose("necessary")}>
            Nur notwendige
          </Button>
          <Button onClick={() => choose("all")}>Alle akzeptieren</Button>
        </div>
      </div>
    </div>
  );
}